/* eslint-disable object-curly-newline,no-plusplus,implicit-arrow-linebreak */
/* global React, G */
import { Grid, List, Typography } from '@mui/material'
import ErrorBoundary from 'ui/Error'
import useMemoRef from '@platform/react/hook/useMemoRef'
import { Suspense, useContext, useEffect, useMemo, useState } from 'react'
import { useMessageAPI, useStyles } from '@platform/react/hook'
import { differenceInMinutes, isBefore, parseISO, startOfDay } from 'date-fns'
import ApplicationContext from '@platform/react/context/application'
import { arrayOf, getFirstItem } from 'lib/util'
import Icon from 'ui/Element/Icon'

/**
 * Returns the MessageList element's current theme styles.
 *
 * @param {Object} theme  the application's current theme
 * @returns {Object}      the MessageList element's styles
 */
export const styles = theme => ({
  root: {
    height: '100%',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    backgroundColor: 'inherit',
    padding: theme.spacing(2),
    margin: [[-1, 1, 1, -1]],
    flex: [[1, 1, '100%']],
  },
  serial: {
    color: theme.palette.text.disabled,
  },
  header: {
    fontSize: '1rem',
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.background.content,
    borderStyle: 'solid',
    borderColor: '#E6E7E8',
    borderWidth: [[0, 0, 1, 0]],
    padding: 16,
  },
  ...theme.custom.messageList,
})

/**
 * Processes the dates of the children messages in order to conditionally display their footer and
 * date messages.
 *
 * @param {Object} p
 * @param {Object[]} p.data                 the current list of messages
 * @param {string} p.sourceId               the current source set for the list
 * @param {string} p.userId                 the current source set for the list
 * @param {Object} p.labels                 translated information labels
 * @param {Object[]} p.systemMessageTypes   list of available system message types
 * @param {string} p.attachmentsGroup       attachment group to add the messages to
 * @param {Object} p.events                 events for the message
 * @param {Object} p.icons                  icons for the message
 * @param {Object} p.MessageItem            the item used to display a user's message
 * @param {Object} p.SystemItem             the item used to display a system message
 * @param {Object} p.DateItem               the item used to display a date message
 * @returns {React.ReactNode[]}
 * @private
 */
const _children = ({
  data,
  sourceId,
  userId,
  systemMessageTypes,
  labels,
  events,
  icons,
  attachmentsGroup,
  ...items
}) => {
  const { MessageItem, SystemItem, DateItem } = items
  return data.reduce((messages, current, index) => {
    const firstItem = index === 0
    const lastItem = index === data.length - 1
    // parsing current message's timestamp
    const { submitTimestamp } = current.value
    const currentTimestamp = submitTimestamp && parseISO(submitTimestamp)
    const currentSourceKey = current.refs.source[0]
    const currentSubmitterKey = getFirstItem(current.refs.submitter)?.key || null
    // gathering previous message's relevant values
    const previous = data[index - 1]
    const previousTimestamp = previous && parseISO(previous.value.submitTimestamp)
    const previousSourceKey = previous?.refs.source[0]
    // const previousSubmitterKey = previous?.refs.submitter?.[0].key
    // gathering next message's relevant values
    const next = data[index + 1]
    const nextTimestamp = next && parseISO(next.value.submitTimestamp)
    const nextSubmitterKey = getFirstItem(next?.refs.submitter)?.key || null
    // checking messages' parameters
    const currentStartOfDay = startOfDay(currentTimestamp)
    const dayChanged = previousTimestamp ? isBefore(previousTimestamp, currentStartOfDay) : true
    const timeExceeded = nextTimestamp && differenceInMinutes(nextTimestamp, currentTimestamp) > 10
    const sameSource = previousSourceKey === currentSourceKey
    const sameSubmitter = nextSubmitterKey === currentSubmitterKey
    // setting flags
    const showFooter = lastItem || timeExceeded || !sameSource || !sameSubmitter
    const showDate = firstItem || dayChanged
    const systemMessage = systemMessageTypes.find(
      systemMessageType => systemMessageType.key === current.value.type,
    )

    // conditionally adding date item
    showDate && messages.push(
      <DateItem
        key={currentTimestamp}
        value={currentTimestamp}
      />,
    )
    // adding message
    messages.push(
      systemMessage
        ? <SystemItem
          key={current.key}
          message={systemMessage}
        />
        : <MessageItem
          {...current}
          key={current.key || index}
          events={events}
          message={{
            id: current.key,
            ...current,
          }}
          icons={icons}
          currentUserId={userId}
          labels={labels}
          attachmentsGroup={attachmentsGroup}
          variant={!currentSourceKey || sourceId === currentSourceKey ? 'primary' : 'secondary'}
          showFooter={showFooter}
        />,
    )
    return messages
  }, [])
}

/**
 * Adds the attachments from the listed messages to the `groupName` attachment group.
 *
 * TODO: Extend Attachment API so that we don't have to do this.
 *
 * @param {Object} state                      the state of the list
 * @param {string} groupName                  the name of the group to attach the attachments to
 * @param {boolean|function} [handler=false]  function for fetching message attachments
 */
export const attachToGroup = (state, groupName, handler = false) => {
  const handlerRef = React.useRef(false)
  const { eventBus } = useContext(ApplicationContext)
  const [attachments, setAttachments] = useState([])
  const { messages } = state

  useEffect(() => {
    // listening for the attachment API to set a new list of images to the attachment group
    eventBus.add(eventBus.type(G.ATTACHMENT, G.DONE, groupName), (event) => {
      const newAttachments = event.detail[G.DATA]
      const changed = newAttachments.filter(newAttachment => !attachments.some(attachment =>
        attachment.key === newAttachment.key))
      setAttachments(changed.length ? newAttachments : [])
    }, { once: true })
    // forcing the attachment API to fire its current list of images from groupName (by sending an
    // empty G.DATA object), so that the listener above gets the attachments
    eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, groupName), { [G.DATA]: {} })
  }, [])

  useEffect(() => {
    if (messages?.length) {
      /**
       * We only want to do this once after the messages
       * are available, therefore we use a reference.
       */
      !handlerRef.current
        && handler
        && handler(messages).then((fetchedAttachments) => {
          handlerRef.current = true

          // Copying the acl information from the fetched attachments to the attachments prop
          // of their message, so that when we render a message, we know if the user has
          // permission to delete its attachments
          for (let i = 0; i < messages.length; i++) {
            const message = messages[i]
            const messageAtts = message?.value?.attachments || []

            for (let y = 0; y < messageAtts.length; y++) {
              const messageAtt = messageAtts[y]
              const fetchedMessageAtt = fetchedAttachments.find(x => x.key === messageAtt.key)
              messageAtt.acl = fetchedMessageAtt.acl
            }
          }

          /**
           * On initial load, the GET to ticket/<id>/listing/attachment returns ticket-
           * as well as message attachments. Because we may already have message
           * attachments, we need to replace them with the ones we enriched
           * with the additional data.
           *
           * Simply calling setAttachments(fetchedAttachments)
           * would override the ticket attachments!
           */
          setAttachments((prevState) => {
            /**
             * First our attachments, then the prevState so that
             * `findIndex` below finds our attachments
             * first.
             */
            const concatenatedState = fetchedAttachments.concat(prevState)
            return concatenatedState.filter(
              (m, i) => i === concatenatedState.findIndex(n => n.key === m.key),
            )
          })
        })

      // Everytime we do this, we need to ensure that we first remove all the message and note
      // attachments from the list, because we may have deleted a message attachment and therefore
      // don't want to show it anymore.
      const parentAttachments = attachments.filter(attachment => attachment.value.parentType !== 'message' && attachment.value.parentType !== 'note')

      // concatenating current group attachments with those from the messages
      const data = parentAttachments?.concat(
        messages
          // accepting only messages that have attachments
          .filter(message => message.value.attachments?.length)
          // mapping each message item to its attachments
          .map(message => message.value.attachments)
          // converting the resulting array of arrays to a single-level array
          .flat()
          // accepting only attachments not already contained by the attachments state
          .filter(messageAttachment => !parentAttachments.some(attachment =>
            messageAttachment.key === attachment.key))
          // setting new = true for attachments that don't have a submitTimestamp
          // e.g. new attachments
          .map(message =>
            (!message.value?.submitTimestamp && { ...message, newAttachment: true }) || message),
      )

      // dispatching the new list attachments (if any)
      data.length && eventBus.dispatch(eventBus.type(G.ATTACHMENT, G.CACHE, groupName), {
        [G.DATA]: {
          [G.DATA]: data,
        },
      })
    }
  }, [state, attachments])
}

/**
 * MessageList component.
 *
 * Listens to messages, processes them and displays them as a list.
 *
 * @param {Object} p                            additional component's properties
 * @param {Object} p.forwardedRef               a reference to the root element
 * @param {Object} p.MessageItem                the item to be used as message
 * @param {Object} p.DateItem                   the item to be used as header
 * @param {Object} p.SystemItem                 the item to be used as system message
 * @param {Object} p.NoRequestItem              the item to be used if there is no request
 * @param {Object} p.value                      value to be set through an action or an event
 * @param {string} p.value.sourceId             the id of the current messages' source
 * @param {string} p.value.userId               the id of the current messages' user
 * @param {Object} p.events                     object containing the component's event handlers
 * @param {Number} p.xs                         number of grids used by the list from xs width
 * @param {Number} p.md                         number of grids used by the list from md width
 * @param {string} p.deleteIcon                 delete icon
 * @param {string} p.editIcon                   edit icon
 * @param {string} p.meLabel                    translation of english 'me'
 * @param {string} p.sendingLabel               text to be shown while a message is being sent
 * @param {string} p.deletedLabel               text to show when a message has been deleted
 * @param {string} p.editedLabel                text to show when a message has been edited
 * @param {string} p.noRequestLabel             text to show when the request has not been published
 * @param {Object} p.contactChannels            information about requester's contactChannels
 * @param {Object} p.contactChannels.prefix     available prefixes for contact channel links
 * @param {Object} p.contactChannels.decorators available prefixes for contact channel links
 * @param {string} p.attachmentsGroup           the name of a group of attachments to which to
 *                                              optionally add the attachments of the messages
 *                                              in the list
 * @returns {JSX.Element}
 */
const MessageList = ({
  forwardedRef,
  MessageItem,
  DateItem,
  SystemItem,
  NoRequestItem,
  value,
  ...props
}) => {
  const {
    meLabel,
    sendingLabel,
    noRequestLabel,
    deletedLabel,
    editedLabel,
    events,
    deleteIcon,
    editIcon,
    attachmentsGroup,
  } = props

  const {
    onCreate,
    getSystemMessageTypes,
    onAttachments,
    onClick = null,
    onDelete = null,
    onChange = null,
  } = events

  const { sourceId, userId } = value || {}
  const classes = useStyles(styles)()
  const { state } = useMessageAPI()
  const { messages } = state

  const systemMessageTypes = getSystemMessageTypes(null)

  const remove = useMemo(() => (
    <Icon
      source={deleteIcon}
      size={'1.5rem'}
      iconPadding={'0.5rem'}
      rounded={true}
    />
  ), [deleteIcon])

  const edit = useMemo(() => (
    <Icon
      source={editIcon}
      size={'1.5rem'}
      iconPadding={'0.5rem'}
      rounded={true}
    />
  ), [editIcon])

  /**
   * Get information about the corresponding request for the current ticket. If we're displaying
   * messages for a request, `request` will be `undefined` and we don't do anything.
   */
  const { type, request, requesterContact, channelLabels } = onCreate?.(null) || {}

  const children = messages.length && _children({
    data: messages,
    sourceId,
    userId,
    systemMessageTypes,
    attachmentsGroup,
    events: {
      onClick,
      onDelete,
      onChange,
    },
    labels: {
      meLabel,
      sendingLabel,
      deletedLabel,
      editedLabel,
    },
    icons: {
      remove,
      edit,
    },
    MessageItem,
    SystemItem,
    DateItem,
  })

  const noData = arrayOf(props?.children || []).find(c => c.key === 'nodata')

  const content = children
    || (type === 'ticket' && !request // we have a ticket with no request ref -> not published to customer
      && <NoRequestItem
        label={noRequestLabel}
        content={requesterContact}
        channelLabels={channelLabels}
        prefixes={props.contactChannels.prefix}
        decorators={props.contactChannels.decorators}
      />
    )
  || noData

  props.attachmentsGroup
    && attachToGroup(
      state,
      props.attachmentsGroup,
      onAttachments,
    )

  useEffect(() => {
    const element = forwardedRef?.current?.parentNode
    setTimeout(() => {
      element && (element.scrollTop = element.scrollHeight - element.clientHeight)
    })
  }, [state])

  return (
    <ErrorBoundary>
      <Grid
        ref={forwardedRef}
        component={List}
        className={classes.root}
        container
        item
        xs={props.xs}
        md={props.md}
      >
        <Suspense fallback={<></>}>
          {content}
        </Suspense>
      </Grid>
    </ErrorBoundary>
  )
}

export default useMemoRef(MessageList, props => [props.children])
